// ── Cohort detail ("/cohorts/:id") — the cohort workspace (Wave 3) ──────────
// The full cohort record: an identity header (name + status + program/company/
// coach) over REAL URL-driven tab routing. The active tab is carried in the URL
// as `?tab=<id>` (deep-linkable; browser back/forward works) via useSearchParams
// — clicking a tab updates the URL and renders THAT tab's own honest read.
//
// Each tab is its OWN component under cohort-detail/ (<500 LOC each), wrapping its
// OWN useHonestQuery -> <HonestState> read. Tabs: Overview / Participants /
// Sessions / Invites / Attendance / Progress / Reports / Recordings /
// Assignments. These are READ tabs — the deep WRITE flows are deferred to their
// owning waves (Participants -> Wave 4, create session/series -> Wave 6, generate
// report -> Wave 7), marked in each tab. No write is fabricated here.
//
// The cohort header read routes through useHonestQuery -> <HonestState>; a gate /
// permission-denied / unavailable / error NEVER reads as an empty cohort shell,
// and a not-found (the 400 "was not found" faked 404 -> "empty" posture) renders
// an honest "Cohort not found", NEVER a cross-record leak. The id is a STRING —
// passed VERBATIM, never Number()'d.
//
// ROLE-GATING: the cohort header edit/complete affordances render only for the
// write capabilities (super_admin/admin/coach). Read-only roles (company_admin
// read-only company cohorts; cohort_lead assigned read-only) see the tabs without
// write controls. The Reports tab is REPORT-EXCLUDED for every role (status only).
// PARTICIPANT reaches the workspace only via My Cohorts and the server pins the
// participant scope; this page never widens it.

import { Badge, Button, Module, ModuleBody, ModuleHead, PageFrame, Tabs, useToast } from "@vjal/ui";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { HonestState, useHonestQuery } from "../../components/honest-state";
import { getCoachingServices } from "../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../hooks/coaching-query-keys";
import { useCohortLifecycleMutations } from "../../hooks/useCoachingQueries";
import { evaluateActionCapability } from "../../domain/capabilities";
import { asLiveApiId } from "../../hooks/useLiveProductData";
import { liveRecordIdFromPath } from "../product-live-rows";
import type { CoachingCohortDto, CoachingOperationResult } from "../../services/coaching-dtos";
import type { ProductScreenContext } from "../product-route-contracts";
import {
  COHORT_DETAIL_TABS,
  COHORT_TAB_LABELS,
  canAccessCohortWorkspace,
  cohortDisplayName,
  cohortStatusLabel,
  cohortStatusTone,
  isValidCohortId,
  normalizeCohortTab
} from "./cohort-detail-helpers";
import { CohortFormModal, type CohortFormValues } from "./cohort-form-modal";
import { CohortCompleteModal } from "./cohort-complete-modal";
import { CohortOverviewTab } from "./cohort-detail/cohort-overview-tab";
import { CohortParticipantsTab } from "./cohort-detail/cohort-participants-tab";
import { CohortSessionsTab } from "./cohort-detail/cohort-sessions-tab";
import { CohortInvitesTab } from "./cohort-detail/cohort-invites-tab";
import { CohortAttendanceTab } from "./cohort-detail/cohort-attendance-tab";
import { CohortProgressTab } from "./cohort-detail/cohort-progress-tab";
import { CohortReportsTab } from "./cohort-detail/cohort-reports-tab";
import { CohortRecordingsTab } from "./cohort-detail/cohort-recordings-tab";
import { CohortAssignmentsTab } from "./cohort-detail/cohort-assignments-tab";

const services = getCoachingServices();

export function CohortDetailPage({ role, routePath }: ProductScreenContext) {
  const toast = useToast();
  const mutations = useCohortLifecycleMutations();
  const allowUpdate = evaluateActionCapability(role, "cohorts:update").allowed;
  const allowComplete = evaluateActionCapability(role, "cohorts:complete").allowed;

  const cohortId = liveRecordIdFromPath(routePath, "cohorts");
  const validId = isValidCohortId(cohortId);

  // ── PARTICIPANT ISOLATION (fail-closed) — blueprint §6 gate #1, FR-PDASH-001.
  // The route manifest keeps `/cohorts/:id` at allRoles (preserved — do NOT
  // alter it), so a participant CAN reach this component. But the admin
  // workspace (roster, sessions, attendance, report status) must FAIL CLOSED for
  // a participant (and any non-workspace role): their cohort context comes ONLY
  // through the Wave-9 Participant Dashboard, never here. We deny BEFORE any read
  // fires (enabled is also gated below) so no roster/report request is even made.
  const workspaceDenied = !canAccessCohortWorkspace(role);

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = normalizeCohortTab(searchParams.get("tab"));

  const [editOpen, setEditOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [completeResult, setCompleteResult] = useState<CoachingOperationResult | null>(null);

  const query = useHonestQuery<CoachingCohortDto>(
    coachingQueryKeys.cohortDetail(cohortId ?? ""),
    () => services.cohorts.detail(cohortId as string),
    { enabled: validId && !workspaceDenied }
  );
  const cohort = query.data ?? null;

  // Fail closed: a non-workspace role (participant/member/etc.) gets a permission
  // -denied posture and NO tab/read renders. This is checked before the cohort
  // header read result is consulted, so the denial never depends on data load.
  if (workspaceDenied) {
    return (
      <section aria-label="Cohort detail" className="col-span-12" data-testid="cohort-detail-page">
        <div className="rounded-xl border border-line bg-white">
          <HonestState
            posture="permission-denied"
            title="You don't have access to this cohort workspace"
            message="The cohort workspace is for coaching staff. Your cohorts are available on your dashboard."
          />
        </div>
      </section>
    );
  }

  function selectTab(tabId: string) {
    const next = new URLSearchParams(searchParams);
    next.set("tab", normalizeCohortTab(tabId));
    setSearchParams(next, { replace: false });
  }

  function submitEdit(values: CohortFormValues) {
    if (!cohort) return Promise.reject(new Error("No cohort loaded"));
    return mutations.update
      .mutateAsync({ cohortId: cohort.id, body: cohortBody(values) })
      .then((updated) => {
        setEditOpen(false);
        mutations.update.reset();
        toast.success(`Cohort ${updated.name ?? values.name} updated`);
        return updated;
      });
  }

  function submitComplete(): Promise<CoachingOperationResult> {
    if (!cohort) return Promise.reject(new Error("No cohort loaded"));
    return mutations.complete.mutateAsync(cohort.id).then((result) => {
      setCompleteResult(result);
      if (result.ok) {
        setCompleteOpen(false);
        mutations.complete.reset();
        toast.success(`Cohort ${cohort.name ?? ""} completed`.trim());
      }
      return result;
    });
  }

  // ── Bad/absent id, or a not-found / non-ready posture → one honest body. ────
  if (!validId || query.posture !== null || !cohort) {
    const posture = !validId ? "empty" : (query.posture ?? "loading");
    const notFound = posture === "empty";
    return (
      <section aria-label="Cohort detail" className="col-span-12" data-testid="cohort-detail-page">
        <div className="rounded-xl border border-line bg-white">
          <HonestState
            posture={posture}
            gate={query.gate}
            reference={query.reference}
            onRetry={query.retry}
            title={notFound ? "Cohort not found" : undefined}
            message={
              notFound
                ? "This cohort doesn't exist, or it isn't in your workspace. It may have been removed."
                : undefined
            }
            canCreate={notFound}
            createAction={
              notFound ? (
                <Link to="/cohorts" className="text-link no-underline hover:underline">
                  Back to cohorts
                </Link>
              ) : undefined
            }
          />
        </div>
      </section>
    );
  }

  const completed = cohort.status === "completed" || cohort.status === "cancelled";
  const tabItems = COHORT_DETAIL_TABS.map((tab) => ({ value: tab, label: COHORT_TAB_LABELS[tab] }));

  return (
    // The route shell owns the page h1 ("Cohort workspace"); the record title is the h2.
    <PageFrame
      titleAs={2}
      title={
        <span className="flex items-center gap-3">
          {cohortDisplayName(cohort.name)}
          <Badge variant={cohortStatusTone(cohort.status)}>{cohortStatusLabel(cohort.status)}</Badge>
        </span>
      }
      subtitle={
        <Link to="/cohorts" className="text-link no-underline hover:underline">
          ← All cohorts
        </Link>
      }
      className="col-span-12"
      titleExactName="Cohort title">
    <section aria-label="Cohort detail" className="flex flex-col gap-4" data-testid="cohort-detail-page">
      <Module span={12}>
        <ModuleHead
          title={
            <span className="flex items-center gap-3">
              {cohortDisplayName(cohort.name)}
              <Badge variant={cohortStatusTone(cohort.status)}>{cohortStatusLabel(cohort.status)}</Badge>
            </span>
          }
          subtitle={
            <Link to="/cohorts" className="text-link no-underline hover:underline">
              ← All cohorts
            </Link>
          }
          titleExactName="Cohort title" />
        <ModuleBody className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <span className="text-sm text-ink-muted">Company: {cohort.company_name?.trim() ? cohort.company_name : "—"}</span>
          <span className="text-sm text-ink-muted">Program: {cohort.program_name?.trim() ? cohort.program_name : "—"}</span>
          <span className="text-sm text-ink-muted">Coach: {cohort.coach_name?.trim() ? cohort.coach_name : "—"}</span>
          {(allowUpdate || allowComplete) && (
            <div className="ml-auto flex gap-2">
              {allowUpdate && (
                <Button variant="secondary" onClick={() => setEditOpen(true)} aria-label="Edit cohort">
                  Edit cohort
                </Button>
              )}
              {allowComplete && (
                <Button
                  variant="primary"
                  onClick={() => {
                    setCompleteResult(null);
                    setCompleteOpen(true);
                  }}
                  disabled={completed}
                  aria-label="Open complete confirmation"
                >
                  Complete cohort
                </Button>
              )}
            </div>
          )}
        </ModuleBody>
      </Module>

      {/* REAL URL-driven tab routing: the tablist drives `?tab=`; each tab below
          renders its OWN honest read. */}
      <Tabs tabs={tabItems} ariaLabel="Cohort detail tabs" value={activeTab} onChange={selectTab} />
      <div data-testid={`cohort-tab-${activeTab}`}>
        {activeTab === "overview" && <CohortOverviewTab cohort={cohort} role={role} />}
        {activeTab === "participants" && <CohortParticipantsTab cohortId={cohort.id} role={role} cohort={cohort} />}
        {activeTab === "sessions" && <CohortSessionsTab cohortId={cohort.id} />}
        {activeTab === "invites" && <CohortInvitesTab cohortId={cohort.id} />}
        {activeTab === "attendance" && <CohortAttendanceTab cohortId={cohort.id} />}
        {activeTab === "progress" && <CohortProgressTab cohortId={cohort.id} />}
        {activeTab === "reports" && <CohortReportsTab cohortId={cohort.id} />}
        {activeTab === "recordings" && <CohortRecordingsTab cohortId={cohort.id} />}
        {activeTab === "assignments" && <CohortAssignmentsTab cohortId={cohort.id} />}
      </div>

      {allowUpdate && (
        <CohortFormModal
          open={editOpen}
          mode="edit"
          cohort={cohort}
          onClose={() => {
            setEditOpen(false);
            mutations.update.reset();
          }}
          onSubmit={submitEdit}
          submitting={mutations.update.isPending}
          submitError={mutations.update.error}
        />
      )}

      {allowComplete && (
        <CohortCompleteModal
          open={completeOpen}
          cohort={cohort}
          onClose={() => {
            setCompleteOpen(false);
            setCompleteResult(null);
            mutations.complete.reset();
          }}
          onConfirm={submitComplete}
          submitting={mutations.complete.isPending}
          submitError={mutations.complete.error}
          lastResult={completeResult}
        />
      )}
    </section>
    </PageFrame>
  );
}

/** Build the edit body — snake_case ids, integer-coerced for the live API. */
function cohortBody(values: CohortFormValues): Record<string, unknown> {
  const body: Record<string, unknown> = { name: values.name, status: values.status };
  if (values.companyId) body.company_id = asLiveApiId(values.companyId);
  if (values.programId) body.program_id = asLiveApiId(values.programId);
  if (values.coachId) body.coach_user_id = asLiveApiId(values.coachId);
  return body;
}
